import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Calendar, LogIn, LogOut as CheckOut, User, Edit, Users, Search, Filter, QrCode, Link2, Copy, Trash2, Phone, CreditCard, Loader2, Camera, Bed, FileText, Download, MessageCircle, Mail, MessageSquare, Eye, Wallet, IndianRupee, Globe } from 'lucide-react';
import { toast } from 'sonner';
import CheckInDialog from '@/components/CheckInDialog';
import CheckoutDialog from '@/components/CheckoutDialog';
import InvoiceReviewDialog from '@/components/InvoiceReviewDialog';
import IdDocumentUpload from '@/components/IdDocumentUpload';
import PhoneInput from '@/components/PhoneInput';
import AddressInput from '@/components/AddressInput';
import SendPortalLinkDialog from '@/components/SendPortalLinkDialog';
import RoomPickerDialog from '@/components/RoomPickerDialog';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

export default function BookingManagement({ isTab, tabId, tabData }) {
  const { openTab } = useTabs();
  const [bookings, setBookings] = useState([]);
  const [guests, setGuests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);
  const [idTypes, setIdTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkInBookingId, setCheckInBookingId] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutBooking, setCheckoutBooking] = useState(null);
  // Initialize statusFilter from tabData if provided (e.g., 'reserved' for Check-in quick action)
  const [statusFilter, setStatusFilter] = useState(tabData?.defaultTab || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selfCheckInDialogOpen, setSelfCheckInDialogOpen] = useState(false);
  const [selfCheckInLink, setSelfCheckInLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [searchingPhone, setSearchingPhone] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  // Part Payment state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [addingPayment, setAddingPayment] = useState(false);
  const [bookingPayments, setBookingPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  // Room assignment dialog states
  const [assignRoomDialogOpen, setAssignRoomDialogOpen] = useState(false);
  const [assignRoomBooking, setAssignRoomBooking] = useState(null);
  const [availableRoomsForAssign, setAvailableRoomsForAssign] = useState([]);
  const [selectedRoomForAssign, setSelectedRoomForAssign] = useState('');
  const [loadingAvailableRooms, setLoadingAvailableRooms] = useState(false);
  const [assigningRoom, setAssigningRoom] = useState(false);
  // Guest Portal Link Dialog
  const [portalLinkDialogOpen, setPortalLinkDialogOpen] = useState(false);
  const [portalLinkBooking, setPortalLinkBooking] = useState(null);
  const [portalLinkGuest, setPortalLinkGuest] = useState(null);
  // Invoice generation
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [invoiceReviewDialogOpen, setInvoiceReviewDialogOpen] = useState(false);
  const [invoiceReviewBooking, setInvoiceReviewBooking] = useState(null);
  // Send invoice dialogs
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [sendInvoiceBooking, setSendInvoiceBooking] = useState(null);
  const [whatsAppNumbers, setWhatsAppNumbers] = useState([{ countryCode: '+91', number: '' }]);
  const [emailAddresses, setEmailAddresses] = useState(['']);
  // Hotel tax settings
  const [hotelTaxSettings, setHotelTaxSettings] = useState({ room_tax: 12 });
  // Cancel booking state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRefundAmount, setCancelRefundAmount] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Country codes for dropdown
  const countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+1', country: 'USA/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+971', country: 'UAE' },
    { code: '+65', country: 'Singapore' },
    { code: '+61', country: 'Australia' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+81', country: 'Japan' },
    { code: '+86', country: 'China' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+974', country: 'Qatar' },
    { code: '+60', country: 'Malaysia' },
    { code: '+66', country: 'Thailand' },
  ];
  
  // New form data structure for inline guest creation
  const [formData, setFormData] = useState({
    room_id: '',  // For backward compatibility - direct room selection
    room_type_id: '',  // New: Book by room type
    smoking_preference: 'no_preference',  // New: smoking, non_smoking, no_preference
    bed_type_id: '',  // New: Preferred bed type
    number_of_rooms: 1,
    check_in: '',
    check_out: '',
    room_rate: '',
    discount: '',
    advance_paid: '',
    notes: '',
    // Primary guest inline data
    primary_guest: {
      phone: '',
      first_name: '',
      last_name: '',
      email: '',
      id_type: '',
      id_number: '',
      id_proof_image: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pin_code: '',
        country: 'India'
      }
    },
    existing_guest_id: null, // If guest found in DB
    // Additional guests - table format
    additional_guests: []
  });

  useEffect(() => {
    loadData();
    loadHotelTaxSettings();
  }, []);

  const loadHotelTaxSettings = async () => {
    try {
      const res = await api.get('/saas/hotels/tax-settings');
      if (res.data?.tax_settings) {
        setHotelTaxSettings(res.data.tax_settings);
      }
    } catch (error) {
      // Use default if tax settings not available
      console.log('Using default tax settings');
    }
  };

  const loadData = async () => {
    try {
      const [bookingsRes, guestsRes, roomsRes, allRoomsRes, roomTypesRes, bedTypesRes, idTypesRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/guests'),
        api.get('/rooms?status=available'),
        api.get('/rooms'),
        api.get('/room-types'),
        api.get('/bed-types'),
        api.get('/id-types')
      ]);
      setBookings(bookingsRes.data);
      setGuests(guestsRes.data);
      setRooms(roomsRes.data);
      setAllRooms(allRoomsRes.data);
      setRoomTypes(roomTypesRes.data);
      setBedTypes(bedTypesRes.data.filter(b => b.is_active));
      setIdTypes(idTypesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Check room availability for dates
  const checkAvailability = async () => {
    if (!formData.check_in || !formData.check_out || !formData.room_type_id) {
      return;
    }
    
    setCheckingAvailability(true);
    try {
      const params = new URLSearchParams({
        check_in: new Date(formData.check_in).toISOString(),
        check_out: new Date(formData.check_out).toISOString(),
        room_type_id: formData.room_type_id,
        number_of_rooms: formData.number_of_rooms.toString()
      });
      
      if (formData.smoking_preference && formData.smoking_preference !== 'no_preference') {
        params.append('smoking_preference', formData.smoking_preference);
      }
      if (formData.bed_type_id && formData.bed_type_id !== 'none') {
        params.append('bed_type_id', formData.bed_type_id);
      }
      
      const res = await api.get(`/rooms/availability?${params.toString()}`);
      setAvailability(res.data);
      
      // Set room rate from room type
      const selectedType = roomTypes.find(rt => rt.id === formData.room_type_id);
      if (selectedType && !formData.room_rate) {
        setFormData(prev => ({...prev, room_rate: selectedType.base_price.toString()}));
      }
    } catch (error) {
      toast.error('Failed to check availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const resetForm = () => {
    setEditingBooking(null);
    setAvailability(null);
    setFormData({
      room_id: '',
      room_type_id: '',
      smoking_preference: 'no_preference',
      bed_type_id: '',
      number_of_rooms: 1,
      check_in: '',
      check_out: '',
      room_rate: '',
      discount: '',
      advance_paid: '',
      notes: '',
      primary_guest: {
        phone: '',
        first_name: '',
        last_name: '',
        email: '',
        id_type: '',
        id_number: '',
        id_proof_image: '',
        address: {
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          pin_code: '',
          country: 'India'
        }
      },
      existing_guest_id: null,
      additional_guests: []
    });
  };

  // Search guest by phone number
  const searchGuestByPhone = useCallback(async (phone) => {
    if (!phone || phone.length < 10) return;
    
    setSearchingPhone(true);
    try {
      const res = await api.get(`/guests/search/phone/${encodeURIComponent(phone)}`);
      if (res.data.found && res.data.guest) {
        const guest = res.data.guest;
        // Split name into first and last name
        const nameParts = (guest.name || '').split(' ');
        const firstName = guest.first_name || nameParts[0] || '';
        const lastName = guest.last_name || nameParts.slice(1).join(' ') || '';
        
        setFormData(prev => ({
          ...prev,
          existing_guest_id: guest.id,
          primary_guest: {
            ...prev.primary_guest,
            phone: guest.phone || phone,
            first_name: firstName,
            last_name: lastName,
            email: guest.email || '',
            id_type: guest.id_type_name || '',
            id_number: guest.id_number || '',
            id_proof_image: guest.id_proof_image || '',
            address: {
              address_line1: guest.address || '',
              address_line2: '',
              city: guest.city || '',
              state: guest.state || '',
              pin_code: guest.pin_code || '',
              country: guest.country || 'India'
            }
          }
        }));
        toast.success('Guest found! Details auto-filled.');
      }
    } catch (error) {
      // Guest not found is OK - will create new
    } finally {
      setSearchingPhone(false);
    }
  }, []);

  // Debounced phone search
  useEffect(() => {
    const phone = formData.primary_guest.phone;
    if (phone && phone.length >= 10 && !formData.existing_guest_id) {
      const timer = setTimeout(() => {
        searchGuestByPhone(phone);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.primary_guest.phone, formData.existing_guest_id, searchGuestByPhone]);

  const generateSelfCheckInLink = async (bookingId) => {
    setGeneratingLink(true);
    try {
      const res = await api.post(`/bookings/${bookingId}/generate-checkin-link`);
      const fullUrl = `${window.location.origin}${res.data.checkin_url}`;
      setSelfCheckInLink({
        code: res.data.checkin_code,
        url: fullUrl,
        hotelName: res.data.hotel_name,
        bookingId: bookingId
      });
      setSelfCheckInDialogOpen(true);
      toast.success('Self check-in link generated!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate check-in link'));
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const openEditBookingTab = (booking) => {
    openTab({
      type: TAB_TYPES.EDIT_BOOKING,
      id: booking.id,
      title: `Edit Booking #${booking.id?.substring(0, 8).toUpperCase()}`,
      data: { booking, onComplete: loadData }
    });
  };

  const openEditDialog = (booking) => {
    // DEPRECATED - Use openEditBookingTab instead
    // Keeping for backward compatibility
    openEditBookingTab(booking);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.primary_guest.phone) {
      toast.error('Primary guest phone number is required');
      return;
    }
    if (!formData.primary_guest.first_name) {
      toast.error('Primary guest first name is required');
      return;
    }
    // Note: ID document is optional during booking, mandatory at check-in
    
    // Validate room type is selected for new bookings (unless legacy room_id mode)
    if (!formData.room_id && !formData.room_type_id && !editingBooking) {
      toast.error('Please select a room type');
      return;
    }
    
    // Check availability if booking by room type
    if (!formData.room_id && formData.room_type_id && availability && !availability.can_book) {
      toast.error(`Not enough rooms available. Only ${availability.total_available} available.`);
      return;
    }
    
    // Validate dates - cannot book past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkInDate = new Date(formData.check_in);
    checkInDate.setHours(0, 0, 0, 0);
    
    if (checkInDate < today && !editingBooking) {
      toast.error('Check-in date cannot be in the past');
      return;
    }
    
    try {
      // Flatten address for API
      const flattenedPrimaryGuest = {
        ...formData.primary_guest,
        address: formData.primary_guest.address?.address_line1 || '',
        city: formData.primary_guest.address?.city || '',
        state: formData.primary_guest.address?.state || '',
        pin_code: formData.primary_guest.address?.pin_code || '',
        country: formData.primary_guest.address?.country || 'India'
      };
      
      const data = {
        room_id: formData.room_id || null,  // Optional for new flow
        room_type_id: formData.room_type_id || null,
        smoking_preference: formData.smoking_preference || 'no_preference',
        bed_type_id: formData.bed_type_id === 'none' ? null : (formData.bed_type_id || null),
        number_of_rooms: formData.number_of_rooms || 1,
        check_in: new Date(formData.check_in).toISOString(),
        check_out: new Date(formData.check_out).toISOString(),
        room_rate: formData.room_rate ? parseFloat(formData.room_rate) : null,
        discount: parseFloat(formData.discount || 0),
        advance_paid: parseFloat(formData.advance_paid || 0),
        notes: formData.notes,
        // Contact details for self check-in OTP
        contact_phone: formData.primary_guest?.phone || null,
        contact_email: formData.primary_guest?.email || null,
        // Inline guest data
        guest_id: formData.existing_guest_id || null,
        primary_guest: flattenedPrimaryGuest,
        additional_guests: formData.additional_guests.filter(ag => ag.first_name && ag.phone)
      };

      if (editingBooking) {
        await api.put(`/bookings/${editingBooking.id}`, data);
        toast.success('Booking updated successfully');
      } else {
        await api.post('/bookings', data);
        toast.success('Booking created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save booking'));
    }
  };

  // Additional guests management
  const addAdditionalGuest = () => {
    setFormData(prev => ({
      ...prev,
      additional_guests: [...prev.additional_guests, {
        first_name: '',
        last_name: '',
        phone: '',
        id_type: '',
        id_number: '',
        id_proof_image: ''
      }]
    }));
  };

  const removeAdditionalGuest = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_guests: prev.additional_guests.filter((_, i) => i !== index)
    }));
  };

  const updateAdditionalGuest = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      additional_guests: prev.additional_guests.map((ag, i) => 
        i === index ? { ...ag, [field]: value } : ag
      )
    }));
  };

  const updatePrimaryGuest = (field, value) => {
    setFormData(prev => ({
      ...prev,
      primary_guest: { ...prev.primary_guest, [field]: value },
      // Clear existing guest id if phone changes
      existing_guest_id: field === 'phone' ? null : prev.existing_guest_id
    }));
  };

  const handleCheckIn = (bookingId) => {
    setCheckInBookingId(bookingId);
    setCheckInDialogOpen(true);
  };

  const handleCheckInComplete = () => {
    loadData();
    setDetailsDialogOpen(false);
  };

  const handleCheckOut = (booking) => {
    setCheckoutBooking(booking);
    setCheckoutDialogOpen(true);
    setDetailsDialogOpen(false);
  };

  const handleCheckoutComplete = () => {
    loadData();
    setCheckoutDialogOpen(false);
    setDetailsDialogOpen(false);
  };

  // Tab-based navigation helpers
  const openCheckoutTab = (booking) => {
    const room = rooms.find(r => r.id === booking.room_id);
    openTab({
      type: TAB_TYPES.CHECKOUT,
      id: booking.id,
      title: `Checkout - Room ${room?.room_number || booking.room_number || 'N/A'}`,
      data: { booking, onComplete: loadData }
    });
    setDetailsDialogOpen(false);
  };

  const openCheckinTab = (booking) => {
    const room = rooms.find(r => r.id === booking.room_id);
    openTab({
      type: TAB_TYPES.CHECKIN,
      id: booking.id,
      title: `Check-in - ${booking.room_type_name || 'Room'}`,
      data: { bookingId: booking.id, onComplete: loadData }
    });
    setDetailsDialogOpen(false);
  };

  const openBookingDetailsTab = (booking) => {
    openTab({
      type: TAB_TYPES.BOOKING_DETAILS,
      id: booking.id,
      title: `Booking #${booking.id?.substring(0, 8).toUpperCase()}`,
      data: { booking, bookingId: booking.id }
    });
  };

  const openNewBookingTab = () => {
    openTab({
      type: TAB_TYPES.NEW_BOOKING,
      id: `new-${Date.now()}`,
      title: 'New Booking',
      data: { onComplete: loadData }
    });
  };

  // Open invoice review dialog to add/edit billing information
  const handleAddInvoiceInfo = (booking) => {
    setInvoiceReviewBooking(booking);
    setInvoiceReviewDialogOpen(true);
  };

  // Download invoice PDF directly
  const handleDownloadInvoice = async (booking) => {
    setDownloadingInvoice(booking.id);
    try {
      // Check if invoice exists, if not create one
      const invoicesRes = await api.get(`/invoices?booking_id=${booking.id}`);
      let invoiceId = null;
      
      if (invoicesRes.data && invoicesRes.data.length > 0) {
        invoiceId = invoicesRes.data[0].id;
      } else {
        // Create new invoice
        const createRes = await api.post(`/invoices/generate/${booking.id}`);
        invoiceId = createRes.data.invoice?.id || createRes.data.id;
      }
      
      if (!invoiceId) {
        throw new Error('Could not get invoice ID');
      }
      
      // Download PDF
      const pdfResponse = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${booking.id.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(getErrorMessage(error, 'Failed to download invoice'));
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Open WhatsApp dialog
  const handleWhatsAppInvoice = (booking) => {
    // Pre-fill with guest phone if available
    const guest = guests.find(g => g.id === booking.guest_id);
    const guestPhone = guest?.phone || '';
    // Try to extract country code if phone starts with +
    let countryCode = '+91';
    let number = guestPhone;
    if (guestPhone.startsWith('+')) {
      const matchedCode = countryCodes.find(c => guestPhone.startsWith(c.code));
      if (matchedCode) {
        countryCode = matchedCode.code;
        number = guestPhone.substring(matchedCode.code.length).trim();
      }
    }
    setWhatsAppNumbers([{ countryCode, number }]);
    setSendInvoiceBooking(booking);
    setWhatsAppDialogOpen(true);
  };

  // Add another WhatsApp number
  const addWhatsAppNumber = () => {
    setWhatsAppNumbers([...whatsAppNumbers, { countryCode: '+91', number: '' }]);
  };

  // Remove a WhatsApp number
  const removeWhatsAppNumber = (index) => {
    if (whatsAppNumbers.length > 1) {
      setWhatsAppNumbers(whatsAppNumbers.filter((_, i) => i !== index));
    }
  };

  // Update WhatsApp number
  const updateWhatsAppNumber = (index, field, value) => {
    const updated = [...whatsAppNumbers];
    updated[index][field] = value;
    setWhatsAppNumbers(updated);
  };

  // Send invoice via WhatsApp
  const confirmSendWhatsApp = async () => {
    // Validate at least one number
    const validNumbers = whatsAppNumbers.filter(n => n.number.trim());
    if (validNumbers.length === 0) {
      toast.error('Please enter at least one mobile number');
      return;
    }
    
    setSendingWhatsApp(sendInvoiceBooking.id);
    try {
      // Check if invoice exists, if not create one
      const invoicesRes = await api.get(`/invoices?booking_id=${sendInvoiceBooking.id}`);
      let invoiceId = null;
      
      if (invoicesRes.data && invoicesRes.data.length > 0) {
        invoiceId = invoicesRes.data[0].id;
      } else {
        // Create new invoice
        const createRes = await api.post(`/invoices/generate/${sendInvoiceBooking.id}`);
        invoiceId = createRes.data.invoice?.id || createRes.data.id;
      }
      
      if (!invoiceId) {
        throw new Error('Could not get invoice ID');
      }
      
      // Format phone numbers
      const phones = validNumbers.map(n => `${n.countryCode}${n.number.trim().replace(/\s/g, '')}`);
      
      // Send via WhatsApp
      const response = await api.post(`/invoices/${invoiceId}/send`, {
        method: 'whatsapp',
        phones: phones
      });
      
      toast.success(response.data.message || `Invoice sent to ${phones.length} number(s) via WhatsApp!`);
      setWhatsAppDialogOpen(false);
      setWhatsAppNumbers([{ countryCode: '+91', number: '' }]);
      setSendInvoiceBooking(null);
    } catch (error) {
      console.error('WhatsApp error:', error);
      toast.error(getErrorMessage(error, 'Failed to send invoice via WhatsApp'));
    } finally {
      setSendingWhatsApp(null);
    }
  };

  // Open Email dialog
  const handleEmailInvoice = (booking) => {
    // Pre-fill with guest email if available
    const guest = guests.find(g => g.id === booking.guest_id);
    setEmailAddresses([guest?.email || '']);
    setSendInvoiceBooking(booking);
    setEmailDialogOpen(true);
  };

  // Add another email address
  const addEmailAddress = () => {
    setEmailAddresses([...emailAddresses, '']);
  };

  // Remove an email address
  const removeEmailAddress = (index) => {
    if (emailAddresses.length > 1) {
      setEmailAddresses(emailAddresses.filter((_, i) => i !== index));
    }
  };

  // Update email address
  const updateEmailAddress = (index, value) => {
    const updated = [...emailAddresses];
    updated[index] = value;
    setEmailAddresses(updated);
  };

  // Send invoice via Email
  const confirmSendEmail = async () => {
    // Validate at least one email
    const validEmails = emailAddresses.filter(e => e.trim());
    if (validEmails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validEmails.filter(e => !emailRegex.test(e.trim()));
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email format: ${invalidEmails.join(', ')}`);
      return;
    }
    
    setSendingEmail(sendInvoiceBooking.id);
    try {
      // Check if invoice exists, if not create one
      const invoicesRes = await api.get(`/invoices?booking_id=${sendInvoiceBooking.id}`);
      let invoiceId = null;
      
      if (invoicesRes.data && invoicesRes.data.length > 0) {
        invoiceId = invoicesRes.data[0].id;
      } else {
        // Create new invoice
        const createRes = await api.post(`/invoices/generate/${sendInvoiceBooking.id}`);
        invoiceId = createRes.data.invoice?.id || createRes.data.id;
      }
      
      if (!invoiceId) {
        throw new Error('Could not get invoice ID');
      }
      
      // Send via Email
      const response = await api.post(`/invoices/${invoiceId}/send`, {
        method: 'email',
        emails: validEmails.map(e => e.trim())
      });
      
      toast.success(response.data.message || `Invoice sent to ${validEmails.length} email(s)!`);
      setEmailDialogOpen(false);
      setEmailAddresses(['']);
      setSendInvoiceBooking(null);
    } catch (error) {
      console.error('Email error:', error);
      toast.error(getErrorMessage(error, 'Failed to send invoice via Email'));
    } finally {
      setSendingEmail(null);
    }
  };

  const openDetailsDialog = async (booking) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
    
    // Fetch additional guests that might not be in the guests array
    if (booking.additional_guest_ids?.length > 0) {
      try {
        const missingGuestIds = booking.additional_guest_ids.filter(
          guestId => !guests.find(g => g.id === guestId)
        );
        
        if (missingGuestIds.length > 0) {
          // Fetch missing guests and add to state
          const guestPromises = missingGuestIds.map(guestId => 
            api.get(`/guests/${guestId}`).catch(() => null)
          );
          const guestResults = await Promise.all(guestPromises);
          const newGuests = guestResults.filter(r => r?.data).map(r => r.data);
          
          if (newGuests.length > 0) {
            setGuests(prev => [...prev, ...newGuests]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch additional guest details:', error);
      }
    }
  };

  // Open assign room dialog (now uses pictorial RoomPickerDialog)
  const openAssignRoomDialog = async (booking) => {
    setAssignRoomBooking(booking);
    setAssignRoomDialogOpen(true);
  };

  // Assign room to booking
  const handleAssignRoom = async () => {
    if (!selectedRoomForAssign) {
      toast.error('Please select a room');
      return;
    }
    
    setAssigningRoom(true);
    try {
      await api.patch(`/bookings/${assignRoomBooking.id}`, {
        room_id: selectedRoomForAssign
      });
      toast.success('Room assigned successfully');
      setAssignRoomDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign room'));
    } finally {
      setAssigningRoom(false);
    }
  };

  // ==================== PART PAYMENTS ====================
  
  // Open payment dialog and load existing payments
  const openPaymentDialog = async (booking) => {
    setPaymentBooking(booking);
    setPaymentAmount('');
    setPaymentMethod('cash');
    setPaymentNotes('');
    setPaymentReference('');
    setPaymentDialogOpen(true);
    setLoadingPayments(true);
    
    try {
      const res = await api.get(`/bookings/${booking.id}/payments`);
      setBookingPayments(res.data.payments || []);
    } catch (error) {
      console.error('Failed to load payments:', error);
      setBookingPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Submit a new payment
  const handleAddPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    setAddingPayment(true);
    try {
      const params = new URLSearchParams({
        amount: paymentAmount,
        payment_method: paymentMethod,
        notes: paymentNotes,
        reference_number: paymentReference
      });
      
      await api.post(`/bookings/${paymentBooking.id}/payments?${params.toString()}`);
      toast.success('Payment added successfully');
      
      // Reload payments
      const res = await api.get(`/bookings/${paymentBooking.id}/payments`);
      setBookingPayments(res.data.payments || []);
      
      // Reset form
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentReference('');
      
      // Refresh bookings list to show updated advance_paid
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add payment'));
    } finally {
      setAddingPayment(false);
    }
  };

  // Helper functions - must be defined before filteredBookings
  const getGuestName = (guestId) => {
    const guest = guests.find(g => g.id === guestId);
    return guest ? guest.name : 'Unknown';
  };

  const getGuestDetails = (guestId) => {
    return guests.find(g => g.id === guestId) || null;
  };

  const getRoomInfo = (roomId, booking = null) => {
    if (roomId) {
      const room = allRooms.find(r => r.id === roomId);
      return room ? `Room ${room.room_number} - ${room.room_type}` : 'Unknown Room';
    }
    // Room not assigned - show room type preference if available
    if (booking?.room_type_name) {
      return `${booking.room_type_name} (Room not assigned)`;
    }
    return 'Room not assigned';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'reserved': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'checked_in': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'checked_out': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Cancel booking handler
  const openCancelDialog = (booking) => {
    setCancelBooking(booking);
    setCancelReason('');
    setCancelRefundAmount(booking.advance_paid?.toString() || '0');
    setCancelDialogOpen(true);
  };

  const handleCancelBooking = async () => {
    if (!cancelBooking) return;
    
    setCancelling(true);
    try {
      const res = await api.post(`/bookings/${cancelBooking.id}/cancel`, {
        reason: cancelReason,
        refund_amount: parseFloat(cancelRefundAmount) || 0
      });
      
      toast.success('Booking cancelled successfully');
      if (res.data.refund_details?.refund_amount > 0) {
        toast.info(`Refund amount: ₹${res.data.refund_details.refund_amount.toLocaleString('en-IN')}`);
      }
      
      setCancelDialogOpen(false);
      setCancelBooking(null);
      loadData(); // Refresh bookings
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel booking'));
    } finally {
      setCancelling(false);
    }
  };

  const canCancelBooking = (status) => {
    return !['cancelled', 'checked_in', 'checked_out'].includes(status);
  };

  // Filter bookings based on status and search
  const filteredBookings = bookings.filter(booking => {
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    const guestName = getGuestName(booking.guest_id).toLowerCase();
    const roomInfo = getRoomInfo(booking.room_id).toLowerCase();
    const bookingId = (booking.id || '').toLowerCase();
    const matchesSearch = !searchQuery || 
      guestName.includes(searchQuery.toLowerCase()) ||
      roomInfo.includes(searchQuery.toLowerCase()) ||
      bookingId.includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  }).sort((a, b) => {
    // Sort by created_at (latest first)
    const dateA = new Date(a.created_at || a.check_in || 0);
    const dateB = new Date(b.created_at || b.check_in || 0);
    return dateB - dateA;
  });

  // Count bookings by status
  const statusCounts = {
    all: bookings.length,
    reserved: bookings.filter(b => b.status === 'reserved').length,
    checked_in: bookings.filter(b => b.status === 'checked_in').length,
    checked_out: bookings.filter(b => b.status === 'checked_out').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length
  };

  // Open Guest Portal Link Dialog
  const openPortalLinkDialog = (booking) => {
    const guest = guests.find(g => g.id === booking.guest_id);
    setPortalLinkBooking(booking);
    setPortalLinkGuest(guest);
    setPortalLinkDialogOpen(true);
  };

  const calculateNights = () => {
    if (!formData.check_in || !formData.check_out) return 0;
    const checkIn = new Date(formData.check_in);
    const checkOut = new Date(formData.check_out);
    return Math.max(1, Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
  };

  const getSelectedRoomRate = () => {
    if (formData.room_rate) return parseFloat(formData.room_rate);
    // Try room type first
    if (formData.room_type_id) {
      const roomType = roomTypes.find(rt => rt.id === formData.room_type_id);
      if (roomType) return roomType.base_price;
    }
    // Fall back to room
    const room = (editingBooking ? allRooms : rooms).find(r => r.id === formData.room_id);
    return room ? room.price_per_night : 0;
  };

  const calculateEstimate = () => {
    const nights = calculateNights();
    const rate = getSelectedRoomRate();
    const subtotal = nights * rate;
    const discount = parseFloat(formData.discount || 0);
    const taxRate = (hotelTaxSettings?.room_tax || 12) / 100;
    const tax = (subtotal - discount) * taxRate;
    return { nights, rate, subtotal, discount, tax, total: subtotal - discount + tax, taxRate: hotelTaxSettings?.room_tax || 12 };
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="booking-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Booking Management</h1>
          <p className="text-gray-500 text-sm mt-1">Manage room bookings and reservations</p>
        </div>
        {/* New Booking Button - Opens in Tab */}
        <Button onClick={openNewBookingTab} className="bg-gradient-orange hover:opacity-90 text-white btn-glow" data-testid="add-booking-button">
          <Plus className="h-4 w-4 mr-2" />
          New Booking
        </Button>
      </div>
      
      {/* Dialog is kept for Edit mode only */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto" data-testid="add-booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-800">{editingBooking ? 'Edit Booking' : 'Create New Booking'}</DialogTitle>
          </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* 1. Dates Section - First */}
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-4">
                  <Calendar className="h-4 w-4 text-orange-600" />
                  Stay Dates
                  {formData.check_in && formData.check_out && (
                    <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-xs ml-2">
                      {calculateNights()} Night(s)
                    </Badge>
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="check_in" className="text-gray-700">Check-in Date *</Label>
                    <Input
                      id="check_in"
                      type="date"
                      value={formData.check_in ? formData.check_in.slice(0, 10) : ''}
                      onChange={(e) => {
                        setFormData({...formData, check_in: e.target.value});
                        setAvailability(null);
                      }}
                      min={new Date().toISOString().slice(0, 10)}
                      required
                      className="border-orange-200 focus:border-orange-400"
                      data-testid="booking-checkin-input"
                    />
                    <p className="text-xs text-gray-500">Cannot be in the past</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="check_out" className="text-gray-700">Check-out Date *</Label>
                    <Input
                      id="check_out"
                      type="date"
                      value={formData.check_out ? formData.check_out.slice(0, 10) : ''}
                      onChange={(e) => {
                        setFormData({...formData, check_out: e.target.value});
                        setAvailability(null);
                      }}
                      min={formData.check_in ? formData.check_in.slice(0, 10) : new Date().toISOString().slice(0, 10)}
                      required
                      className="border-orange-200 focus:border-orange-400"
                      data-testid="booking-checkout-input"
                    />
                    <p className="text-xs text-gray-500">Must be after check-in</p>
                  </div>
                </div>
              </div>

              {/* 2. Room Type Selection - Second */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-4">
                  <Bed className="h-4 w-4 text-blue-600" />
                  Room Preferences
                  <span className="text-xs text-gray-500 font-normal">(Room assigned at check-in)</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Room Type *</Label>
                    <Select 
                      value={formData.room_type_id}
                      onValueChange={(value) => {
                        const roomType = roomTypes.find(rt => rt.id === value);
                        setFormData({
                          ...formData, 
                          room_type_id: value,
                          room_rate: roomType?.base_price?.toString() || formData.room_rate
                        });
                        setAvailability(null);
                      }}
                    >
                      <SelectTrigger className="border-blue-200" data-testid="booking-roomtype-select">
                        <SelectValue placeholder="Select room type" />
                      </SelectTrigger>
                      <SelectContent>
                        {roomTypes.map(rt => (
                          <SelectItem key={rt.id} value={rt.id}>
                            {rt.name} (₹{rt.base_price}/night)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Bed Type</Label>
                    <Select 
                      value={formData.bed_type_id}
                      onValueChange={(value) => {
                        setFormData({...formData, bed_type_id: value});
                        setAvailability(null);
                      }}
                    >
                      <SelectTrigger className="border-blue-200" data-testid="booking-bedtype-select">
                        <SelectValue placeholder="No preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No preference</SelectItem>
                        {bedTypes.map(bt => (
                          <SelectItem key={bt.id} value={bt.id}>{bt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Smoking Preference</Label>
                    <Select 
                      value={formData.smoking_preference}
                      onValueChange={(value) => {
                        setFormData({...formData, smoking_preference: value});
                        setAvailability(null);
                      }}
                    >
                      <SelectTrigger className="border-blue-200" data-testid="booking-smoking-select">
                        <SelectValue placeholder="No preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no_preference">No preference</SelectItem>
                        <SelectItem value="non_smoking">Non-Smoking</SelectItem>
                        <SelectItem value="smoking">Smoking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="room_rate" className="text-gray-700">Room Rate/Night</Label>
                    <Input
                      id="room_rate"
                      type="number"
                      placeholder="Auto from room type"
                      value={formData.room_rate}
                      onChange={(e) => setFormData({...formData, room_rate: e.target.value})}
                      className="border-blue-200 focus:border-blue-400"
                      data-testid="booking-rate-input"
                    />
                  </div>
                </div>

                {/* Availability Check */}
                {formData.room_type_id && formData.check_in && formData.check_out && (
                  <div className="mt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={checkAvailability}
                      disabled={checkingAvailability}
                      className="border-blue-300 text-blue-600 hover:bg-blue-100"
                    >
                      {checkingAvailability ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Check Availability
                        </>
                      )}
                    </Button>
                    
                    {availability && (
                      <div className={`mt-3 p-3 rounded-lg ${availability.can_book ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        {availability.can_book ? (
                          <p className="text-green-700 text-sm">
                            ✓ {availability.total_available} room(s) available for selected dates and preferences
                          </p>
                        ) : (
                          <p className="text-red-700 text-sm">
                            ✗ Only {availability.total_available} room(s) available. Need {formData.number_of_rooms}.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 3. Primary Guest Section - After Room Preferences */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-600" />
                  Primary Guest Details
                  {formData.existing_guest_id && (
                    <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">Existing Guest</Badge>
                  )}
                </h3>
                
                {/* Mobile Number - First Field */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Mobile Number *
                    </Label>
                    <div className="relative">
                      <PhoneInput
                        value={formData.primary_guest.phone}
                        onChange={(value) => updatePrimaryGuest('phone', value)}
                        placeholder="98765 43210"
                        required
                        data-testid="primary-guest-phone"
                      />
                      {searchingPhone && (
                        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500">Enter phone to auto-fill existing guest</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">First Name *</Label>
                    <Input
                      value={formData.primary_guest.first_name}
                      onChange={(e) => updatePrimaryGuest('first_name', e.target.value)}
                      placeholder="First name"
                      required
                      className="border-blue-200 focus:border-blue-400"
                      data-testid="primary-guest-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Last Name</Label>
                    <Input
                      value={formData.primary_guest.last_name}
                      onChange={(e) => updatePrimaryGuest('last_name', e.target.value)}
                      placeholder="Last name"
                      className="border-blue-200 focus:border-blue-400"
                      data-testid="primary-guest-last-name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Email</Label>
                    <Input
                      type="email"
                      value={formData.primary_guest.email}
                      onChange={(e) => updatePrimaryGuest('email', e.target.value)}
                      placeholder="email@example.com"
                      className="border-blue-200 focus:border-blue-400"
                      data-testid="primary-guest-email"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-gray-700">Address</Label>
                    <AddressInput
                      value={formData.primary_guest.address}
                      onChange={(value) => updatePrimaryGuest('address', value)}
                      data-testid="primary-guest-address"
                    />
                  </div>
                </div>
                
                {/* ID Document - Optional at booking, mandatory at check-in */}
                <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="space-y-2">
                    <Label className="text-gray-700 flex items-center gap-1">
                      <CreditCard className="h-3 w-3 text-gray-500" />
                      ID Document Type
                      <span className="text-xs text-gray-400">(Optional)</span>
                    </Label>
                    <Select 
                      value={formData.primary_guest.id_type} 
                      onValueChange={(v) => updatePrimaryGuest('id_type', v)}
                    >
                      <SelectTrigger className="border-gray-200" data-testid="primary-guest-id-type">
                        <SelectValue placeholder="Select ID Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {idTypes.map(type => (
                          <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                        ))}
                        <SelectItem value="Aadhaar">Aadhaar Card</SelectItem>
                        <SelectItem value="Passport">Passport</SelectItem>
                        <SelectItem value="Driving License">Driving License</SelectItem>
                        <SelectItem value="Voter ID">Voter ID</SelectItem>
                        <SelectItem value="PAN Card">PAN Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">ID Number <span className="text-xs text-gray-400">(Optional)</span></Label>
                    <Input
                      value={formData.primary_guest.id_number}
                      onChange={(e) => updatePrimaryGuest('id_number', e.target.value)}
                      placeholder="Enter ID number"
                      className="border-gray-200 focus:border-gray-400"
                      data-testid="primary-guest-id-number"
                    />
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-1">* ID documents are required at check-in</p>
                {/* ID Document Photo Upload */}
                <div className="mt-3">
                  <IdDocumentUpload
                    value={formData.primary_guest.id_proof_image}
                    onChange={(url) => updatePrimaryGuest('id_proof_image', url)}
                    label="ID Document Photo (Recommended)"
                    guestId={formData.existing_guest_id}
                  />
                </div>
              </div>

              {/* 4. Additional Guests Section - Table Format */}
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" />
                    Additional Guests
                    <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                  </h3>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={addAdditionalGuest}
                    className="border-purple-300 text-purple-600 hover:bg-purple-100"
                    data-testid="add-additional-guest-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Guest
                  </Button>
                </div>
                
                {formData.additional_guests.length > 0 ? (
                  <div className="border border-purple-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-purple-100">
                          <TableHead className="text-gray-700">First Name</TableHead>
                          <TableHead className="text-gray-700">Last Name</TableHead>
                          <TableHead className="text-gray-700">Mobile</TableHead>
                          <TableHead className="text-gray-700">ID Type</TableHead>
                          <TableHead className="text-gray-700">ID Number</TableHead>
                          <TableHead className="text-gray-700">ID Photo</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.additional_guests.map((guest, index) => (
                          <TableRow key={index} className="bg-white">
                            <TableCell>
                              <Input
                                value={guest.first_name}
                                onChange={(e) => updateAdditionalGuest(index, 'first_name', e.target.value)}
                                placeholder="First name"
                                className="h-8 text-sm border-purple-200"
                                data-testid={`additional-guest-${index}-first-name`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={guest.last_name}
                                onChange={(e) => updateAdditionalGuest(index, 'last_name', e.target.value)}
                                placeholder="Last name"
                                className="h-8 text-sm border-purple-200"
                              />
                            </TableCell>
                            <TableCell>
                              <PhoneInput
                                value={guest.phone}
                                onChange={(value) => updateAdditionalGuest(index, 'phone', value)}
                                placeholder="Mobile"
                                className="h-8 text-sm"
                                data-testid={`additional-guest-${index}-phone`}
                              />
                            </TableCell>
                            <TableCell>
                              <Select 
                                value={guest.id_type} 
                                onValueChange={(v) => updateAdditionalGuest(index, 'id_type', v)}
                              >
                                <SelectTrigger className="h-8 text-sm border-purple-200">
                                  <SelectValue placeholder="ID Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {idTypes.map(type => (
                                    <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                                  ))}
                                  <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                                  <SelectItem value="Passport">Passport</SelectItem>
                                  <SelectItem value="DL">DL</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                value={guest.id_number}
                                onChange={(e) => updateAdditionalGuest(index, 'id_number', e.target.value)}
                                placeholder="ID Number"
                                className="h-8 text-sm border-purple-200"
                              />
                            </TableCell>
                            <TableCell>
                              {guest.id_proof_image ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-green-600">✓</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateAdditionalGuest(index, 'id_proof_image', '')}
                                    className="h-6 px-2 text-xs text-red-500"
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*';
                                    input.onchange = async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const fd = new FormData();
                                        fd.append('file', file);
                                        try {
                                          const res = await api.post('/upload/id-document', fd, {
                                            headers: { 'Content-Type': 'multipart/form-data' }
                                          });
                                          updateAdditionalGuest(index, 'id_proof_image', res.data.document_url);
                                          toast.success('ID uploaded');
                                        } catch {
                                          toast.error('Upload failed');
                                        }
                                      }
                                    };
                                    input.click();
                                  }}
                                  className="h-7 px-2 text-xs border-purple-200"
                                >
                                  <Camera className="h-3 w-3 mr-1" />
                                  Add
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeAdditionalGuest(index)}
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No additional guests. Click &quot;Add Guest&quot; to add more guests to this booking.
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  * ID documents for additional guests are optional
                </p>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="discount" className="text-gray-700">Discount (₹)</Label>
                  <Input
                    id="discount"
                    type="number"
                    placeholder="0"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    className="border-orange-200 focus:border-orange-400"
                    data-testid="booking-discount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="advance" className="text-gray-700">Advance Payment</Label>
                  <Input
                    id="advance"
                    type="number"
                    placeholder="0"
                    value={formData.advance_paid}
                    onChange={(e) => setFormData({...formData, advance_paid: e.target.value})}
                    className="border-orange-200 focus:border-orange-400"
                    data-testid="booking-advance-input"
                  />
                </div>
              </div>

              {/* Estimate */}
              {formData.check_in && formData.check_out && (formData.room_id || formData.room_type_id) && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Booking Estimate</p>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Nights</p>
                      <p className="font-semibold">{calculateEstimate().nights}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Rate</p>
                      <p className="font-semibold">₹{calculateEstimate().rate}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Tax ({calculateEstimate().taxRate}%)</p>
                      <p className="font-semibold">₹{calculateEstimate().tax.toFixed(0)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total</p>
                      <p className="font-bold text-orange-600">₹{calculateEstimate().total.toFixed(0)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-gray-700">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Special requests, notes..."
                  className="border-orange-200 focus:border-orange-400"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-orange hover:opacity-90 text-white" data-testid="submit-booking-button">
                  {editingBooking ? 'Update Booking' : 'Create Booking'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

      {/* Filters Section */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        {/* Status Filter Tabs */}
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full md:w-auto">
          <TabsList className="bg-gray-100 p-1">
            <TabsTrigger value="all" className="data-[state=active]:bg-white">
              All ({statusCounts.all})
            </TabsTrigger>
            <TabsTrigger value="reserved" className="data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700">
              Reserved ({statusCounts.reserved})
            </TabsTrigger>
            <TabsTrigger value="checked_in" className="data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700">
              Checked In ({statusCounts.checked_in})
            </TabsTrigger>
            <TabsTrigger value="checked_out" className="data-[state=active]:bg-gray-200 data-[state=active]:text-gray-700">
              Checked Out ({statusCounts.checked_out})
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
              Cancelled ({statusCounts.cancelled})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search */}
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search guest or room..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-gray-200"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
        </div>
      ) : filteredBookings.length > 0 ? (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <Card 
              key={booking.id} 
              className="border-orange-100 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer" 
              data-testid={`booking-card-${booking.id}`}
              onClick={() => openBookingDetailsTab(booking)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-100 to-purple-100 flex items-center justify-center">
                      <Calendar className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg text-gray-800">{getGuestName(booking.guest_id)}</h3>
                            <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">
                              #{booking.id?.substring(0, 8).toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{getRoomInfo(booking.room_id, booking)}</p>
                          {booking.additional_guest_ids && booking.additional_guest_ids.length > 0 && (
                            <p className="text-xs text-purple-600 mt-1">
                              +{booking.additional_guest_ids.length} additional guest(s)
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); openBookingDetailsTab(booking); }} 
                            className="h-8 px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {/* Booking Source Badge */}
                          {booking.source === 'portal' ? (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200" data-testid={`booking-source-${booking.id}`}>
                              <Globe className="h-3 w-3 mr-1" />
                              Portal
                            </Badge>
                          ) : booking.source === 'walk_in' ? (
                            <Badge className="bg-gray-100 text-gray-700 border border-gray-200" data-testid={`booking-source-${booking.id}`}>
                              Walk-in
                            </Badge>
                          ) : booking.source === 'phone' ? (
                            <Badge className="bg-purple-100 text-purple-700 border border-purple-200" data-testid={`booking-source-${booking.id}`}>
                              <Phone className="h-3 w-3 mr-1" />
                              Phone
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-700 border border-orange-200" data-testid={`booking-source-${booking.id}`}>
                              Direct
                            </Badge>
                          )}
                          <Badge className={`${getStatusColor(booking.status)} border`} data-testid={`booking-status-${booking.id}`}>
                            {booking.status.replace('_', ' ')}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={(e) => { e.stopPropagation(); openEditDialog(booking); }} 
                            className="h-8 w-8 text-gray-500 hover:text-orange-600"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Check-in</p>
                          <p className="font-medium text-gray-800">{new Date(booking.check_in_date || booking.check_in).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Check-out</p>
                          <p className="font-medium text-gray-800">{new Date(booking.check_out_date || booking.check_out).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Nights</p>
                          <p className="font-medium text-gray-800">
                            {booking.total_nights || (() => {
                              const checkIn = new Date(booking.check_in_date || booking.check_in);
                              const checkOut = new Date(booking.check_out_date || booking.check_out);
                              const nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                              return isNaN(nights) || nights < 1 ? '-' : nights;
                            })()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total</p>
                          <p className="font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono' }}>₹{booking.total_amount?.toFixed(0) || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Advance</p>
                          <p className="font-semibold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>₹{booking.advance_paid || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                    {booking.status === 'reserved' && (
                      <>
                        {!booking.room_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAssignRoomDialog(booking)}
                            className="border-amber-300 text-amber-600 hover:bg-amber-50"
                            data-testid={`assign-room-button-${booking.id}`}
                          >
                            <Bed className="h-4 w-4 mr-1" />
                            Assign Room
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPaymentDialog(booking)}
                          className="border-teal-300 text-teal-600 hover:bg-teal-50"
                          data-testid={`add-payment-button-${booking.id}`}
                        >
                          <Wallet className="h-4 w-4 mr-1" />
                          Add Payment
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateSelfCheckInLink(booking.id)}
                          disabled={generatingLink}
                          className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                          data-testid={`self-checkin-button-${booking.id}`}
                        >
                          <QrCode className="h-4 w-4 mr-1" />
                          Self Check-in
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openCheckinTab(booking)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`checkin-button-${booking.id}`}
                        >
                          <LogIn className="h-4 w-4 mr-1" />
                          Check-in
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openCancelDialog(booking)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`cancel-booking-button-${booking.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </>
                    )}
                    {booking.status === 'checked_in' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPaymentDialog(booking)}
                          className="border-teal-300 text-teal-600 hover:bg-teal-50"
                          data-testid={`add-payment-button-${booking.id}`}
                        >
                          <Wallet className="h-4 w-4 mr-1" />
                          Add Payment
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPortalLinkDialog(booking)}
                          className="border-violet-300 text-violet-600 hover:bg-violet-50"
                          data-testid={`portal-link-button-${booking.id}`}
                        >
                          <Link2 className="h-4 w-4 mr-1" />
                          Guest Portal
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openCheckoutTab(booking)}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid={`checkout-button-${booking.id}`}
                        >
                          <CheckOut className="h-4 w-4 mr-1" />
                          Check-out
                        </Button>
                      </>
                    )}
                    {booking.status === 'checked_out' && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddInvoiceInfo(booking)}
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                          data-testid={`add-invoice-info-button-${booking.id}`}
                          title="Add/Edit Invoice Information"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Invoice Info
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDownloadInvoice(booking)}
                          disabled={downloadingInvoice === booking.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`download-invoice-button-${booking.id}`}
                          title="Download Invoice PDF"
                        >
                          {downloadingInvoice === booking.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEmailInvoice(booking)}
                          disabled={sendingEmail === booking.id}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`email-invoice-button-${booking.id}`}
                          title="Send Invoice via Email"
                        >
                          {sendingEmail === booking.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleWhatsAppInvoice(booking)}
                          disabled={sendingWhatsApp === booking.id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`whatsapp-invoice-button-${booking.id}`}
                          title="Send Invoice via WhatsApp"
                        >
                          {sendingWhatsApp === booking.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MessageCircle className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !loading && (
        <Card className="border-orange-100">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-orange-300 mb-4" />
            <p className="text-gray-500">
              {bookings.length === 0 
                ? 'No bookings yet' 
                : `No ${statusFilter === 'all' ? '' : statusFilter.replace('_', ' ')} bookings found`}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {bookings.length === 0 
                ? 'Click "New Booking" to create a reservation'
                : 'Try adjusting your filters'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Self Check-in Link Dialog */}
      <Dialog open={selfCheckInDialogOpen} onOpenChange={setSelfCheckInDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-white overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <QrCode className="h-5 w-5 text-indigo-600" />
              Self Check-in Link
            </DialogTitle>
          </DialogHeader>
          {selfCheckInLink && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Share this link with the guest for self check-in:
              </p>
              
              <div className="p-4 bg-gray-900 rounded-lg">
                <p className="text-xs text-gray-400 mb-1 font-medium uppercase">Check-in Code</p>
                <p className="text-3xl font-mono font-bold text-white tracking-widest">
                  {selfCheckInLink.code}
                </p>
              </div>
              
              <div className="p-3 bg-gray-100 rounded-lg border border-gray-300">
                <p className="text-xs text-gray-600 mb-1 font-medium">Link URL</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-900 font-mono truncate flex-1 min-w-0">
                    {selfCheckInLink.url}
                  </p>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => copyToClipboard(selfCheckInLink.url)}
                    className="text-gray-700 hover:bg-gray-200 h-6 w-6 shrink-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-gray-800 mb-2">Send link to guest via:</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await api.post(`/self-checkin/send-link`, {
                          booking_id: selfCheckInLink.bookingId,
                          channels: ['email'],
                          base_url: window.location.origin
                        });
                        toast.success('Email sent!');
                      } catch (err) {
                        toast.error('Failed');
                      }
                    }}
                    className="flex-1 border-blue-400 text-blue-700 hover:bg-blue-50"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await api.post(`/self-checkin/send-link`, {
                          booking_id: selfCheckInLink.bookingId,
                          channels: ['sms'],
                          base_url: window.location.origin
                        });
                        toast.success('SMS sent!');
                      } catch (err) {
                        toast.error('Failed');
                      }
                    }}
                    className="flex-1 border-orange-400 text-orange-700 hover:bg-orange-50"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    SMS
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await api.post(`/self-checkin/send-link`, {
                          booking_id: selfCheckInLink.bookingId,
                          channels: ['whatsapp'],
                          base_url: window.location.origin
                        });
                        toast.success('WhatsApp sent!');
                      } catch (err) {
                        toast.error('Failed');
                      }
                    }}
                    className="flex-1 border-green-500 text-green-700 hover:bg-green-50"
                  >
                    <Phone className="h-4 w-4 mr-1" />
                    WhatsApp
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => copyToClipboard(selfCheckInLink.url)}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(selfCheckInLink.url, '_blank')}
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Link2 className="h-4 w-4 mr-1" />
                  Preview
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Guest can use this link on their mobile device
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Check-In Dialog */}
      <CheckInDialog
        open={checkInDialogOpen}
        onOpenChange={setCheckInDialogOpen}
        bookingId={checkInBookingId}
        onCheckInComplete={handleCheckInComplete}
      />

      {/* Checkout Dialog */}
      <CheckoutDialog
        open={checkoutDialogOpen}
        onOpenChange={setCheckoutDialogOpen}
        booking={checkoutBooking}
        onCheckoutComplete={handleCheckoutComplete}
      />

      {/* Booking Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-600" />
              Booking Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <Badge className={`${getStatusColor(selectedBooking.status)} border text-sm px-3 py-1`}>
                  {selectedBooking.status.replace('_', ' ').toUpperCase()}
                </Badge>
                <span className="text-sm text-gray-500">
                  Booking ID: {selectedBooking.id?.substring(0, 8).toUpperCase()}
                </span>
              </div>

              {/* Primary Guest Info - Full Details */}
              {(() => {
                const guest = getGuestDetails(selectedBooking.guest_id);
                return (
                  <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <h4 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Primary Guest Information
                    </h4>
                    <p className="font-semibold text-lg text-gray-800">{guest?.name || 'Unknown'}</p>
                    
                    {/* Contact Details */}
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      {guest?.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="h-4 w-4 text-blue-500" />
                          <span>{guest.phone}</span>
                        </div>
                      )}
                      {guest?.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <span className="h-4 w-4 text-green-500">✉️</span>
                          <span>{guest.email}</span>
                        </div>
                      )}
                      {guest?.address && (
                        <div className="flex items-start gap-2 text-gray-600">
                          <span className="h-4 w-4 text-purple-500 mt-0.5">📍</span>
                          <span>{guest.address}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* ID Details */}
                    {(guest?.id_type_name || guest?.id_number) && (
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">ID Document</p>
                        <div className="flex items-center gap-2 text-sm">
                          <CreditCard className="h-4 w-4 text-orange-500" />
                          <span className="font-medium">{guest?.id_type_name || 'ID'}</span>
                          {guest?.id_number && (
                            <span className="text-gray-600">: {guest.id_number}</span>
                          )}
                        </div>
                        {guest?.id_proof_image && (
                          <a 
                            href={guest.id_proof_image.startsWith('http') ? guest.id_proof_image : `${process.env.REACT_APP_BACKEND_URL}${guest.id_proof_image}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                          >
                            <Camera className="h-3 w-3" />
                            View ID Document
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Additional Guests */}
              {selectedBooking.additional_guest_ids?.length > 0 && (
                <div className="p-4 bg-purple-50 rounded-lg space-y-3">
                  <h4 className="text-sm font-medium text-purple-700 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Additional Guests ({selectedBooking.additional_guest_ids.length})
                  </h4>
                  {selectedBooking.additional_guest_ids.map((guestId, index) => {
                    const addGuest = getGuestDetails(guestId);
                    return (
                      <div key={guestId} className="p-3 bg-white rounded-lg border border-purple-100">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs text-gray-500">Name</p>
                            <p className="font-medium text-gray-800">
                              {addGuest?.name || `${addGuest?.first_name || ''} ${addGuest?.last_name || ''}`.trim() || `Guest ${index + 1}`}
                            </p>
                          </div>
                          {addGuest?.phone && (
                            <div>
                              <p className="text-xs text-gray-500">Phone</p>
                              <p className="font-medium text-gray-800">{addGuest.phone}</p>
                            </div>
                          )}
                          {(addGuest?.id_type || addGuest?.id_type_name) && (
                            <div>
                              <p className="text-xs text-gray-500">ID Type</p>
                              <p className="font-medium text-gray-800">{addGuest.id_type_name || addGuest.id_type}</p>
                            </div>
                          )}
                          {addGuest?.id_number && (
                            <div>
                              <p className="text-xs text-gray-500">ID Number</p>
                              <p className="font-medium text-gray-800">{addGuest.id_number}</p>
                            </div>
                          )}
                        </div>
                        {addGuest?.id_proof_image && (
                          <a 
                            href={addGuest.id_proof_image.startsWith('http') ? addGuest.id_proof_image : `${process.env.REACT_APP_BACKEND_URL}${addGuest.id_proof_image}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline mt-2"
                          >
                            <Camera className="h-3 w-3" />
                            View ID Document
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Room Info */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">Room</p>
                <p className="font-semibold text-gray-800">{getRoomInfo(selectedBooking.room_id, selectedBooking)}</p>
              </div>

              {/* Booking Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">Check-in</p>
                  <p className="font-semibold text-gray-800">
                    {new Date(selectedBooking.check_in_date || selectedBooking.check_in).toLocaleDateString('en-IN', { 
                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                    })}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Check-out</p>
                  <p className="font-semibold text-gray-800">
                    {new Date(selectedBooking.check_out_date || selectedBooking.check_out).toLocaleDateString('en-IN', { 
                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>

              {/* Payment Info */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="text-sm font-medium text-orange-700 mb-3">Payment Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Room Rate × {selectedBooking.total_nights || (() => {
                      const checkIn = new Date(selectedBooking.check_in_date || selectedBooking.check_in);
                      const checkOut = new Date(selectedBooking.check_out_date || selectedBooking.check_out);
                      const nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                      return isNaN(nights) || nights < 1 ? 1 : nights;
                    })()} nights</span>
                    <span className="font-medium">₹{((selectedBooking.total_amount || 0) / (1 + (hotelTaxSettings?.room_tax || 12) / 100)).toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax ({hotelTaxSettings?.room_tax || 12}%)</span>
                    <span className="font-medium">₹{((selectedBooking.total_amount || 0) - (selectedBooking.total_amount || 0) / (1 + (hotelTaxSettings?.room_tax || 12) / 100)).toFixed(0)}</span>
                  </div>
                  <div className="border-t border-orange-200 pt-2 flex justify-between">
                    <span className="font-semibold text-gray-700">Total Amount</span>
                    <span className="font-bold text-orange-600 text-lg">₹{selectedBooking.total_amount?.toFixed(0) || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Advance Paid</span>
                    <span className="font-medium text-emerald-600">- ₹{selectedBooking.advance_paid || 0}</span>
                  </div>
                  <div className="border-t border-orange-200 pt-2 flex justify-between">
                    <span className="font-semibold text-gray-700">Balance Due</span>
                    <span className="font-bold text-red-600">
                      ₹{((selectedBooking.total_amount || 0) - (selectedBooking.advance_paid || 0)).toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedBooking.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Notes</p>
                  <p className="text-sm text-gray-700">{selectedBooking.notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                {selectedBooking.status === 'reserved' && (
                  <Button
                    onClick={() => {
                      setDetailsDialogOpen(false);
                      handleCheckIn(selectedBooking.id);
                    }}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Check-in Guest
                  </Button>
                )}
                {selectedBooking.status === 'checked_in' && (
                  <Button
                    onClick={() => handleCheckOut(selectedBooking)}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <CheckOut className="h-4 w-4 mr-2" />
                    Check-out Guest
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    openEditDialog(selectedBooking);
                  }}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Booking
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pictorial Room Picker Dialog */}
      <RoomPickerDialog
        open={assignRoomDialogOpen}
        onOpenChange={setAssignRoomDialogOpen}
        booking={assignRoomBooking ? {
          ...assignRoomBooking,
          guest_name: getGuestName(assignRoomBooking.guest_id)
        } : null}
        onRoomAssigned={loadData}
      />

      {/* Part Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Wallet className="h-5 w-5 text-teal-600" />
              Add Payment
            </DialogTitle>
            <DialogDescription>
              Record a partial payment for this booking
            </DialogDescription>
          </DialogHeader>
          
          {paymentBooking && (
            <div className="space-y-4 py-4">
              {/* Booking Summary */}
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-800">{getGuestName(paymentBooking.guest_id)}</p>
                    <p className="text-sm text-gray-600">{paymentBooking.room_type_name || 'Room'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Total Amount</p>
                    <p className="font-bold text-gray-800">₹{paymentBooking.total_amount?.toFixed(0) || 0}</p>
                  </div>
                </div>
                <div className="flex justify-between mt-2 pt-2 border-t border-teal-200">
                  <span className="text-sm text-gray-600">Paid so far</span>
                  <span className="font-semibold text-emerald-600">₹{paymentBooking.advance_paid || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Balance due</span>
                  <span className="font-semibold text-orange-600">
                    ₹{((paymentBooking.total_amount || 0) - (paymentBooking.advance_paid || 0)).toFixed(0)}
                  </span>
                </div>
              </div>

              {/* Payment History */}
              {loadingPayments ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-teal-500" />
                  <span className="ml-2 text-gray-500">Loading payment history...</span>
                </div>
              ) : bookingPayments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-1">
                    <IndianRupee className="h-3 w-3" />
                    Payment History
                  </Label>
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Amount</TableHead>
                          <TableHead className="text-xs">Method</TableHead>
                          <TableHead className="text-xs">By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookingPayments.map((payment) => (
                          <TableRow key={payment.id} className="text-sm">
                            <TableCell className="py-2">
                              {new Date(payment.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="py-2 font-medium text-emerald-600">
                              ₹{payment.amount?.toFixed(0)}
                            </TableCell>
                            <TableCell className="py-2 capitalize">
                              {payment.payment_method}
                            </TableCell>
                            <TableCell className="py-2 text-xs text-gray-500">
                              {payment.created_by_name || 'Staff'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* New Payment Form */}
              <div className="space-y-4 pt-2 border-t">
                <Label className="text-gray-700 font-semibold">Add New Payment</Label>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Amount *</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="number"
                        placeholder="0"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        className="pl-9 border-teal-200 focus:border-teal-400"
                        data-testid="payment-amount-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="border-teal-200" data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Reference Number</Label>
                  <Input
                    placeholder="Transaction ID, cheque number, etc."
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    className="border-teal-200 focus:border-teal-400"
                    data-testid="payment-reference-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Notes</Label>
                  <Input
                    placeholder="Optional payment notes"
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    className="border-teal-200 focus:border-teal-400"
                    data-testid="payment-notes-input"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleAddPayment}
              disabled={addingPayment || !paymentAmount}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="submit-payment-btn"
            >
              {addingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Wallet className="h-4 w-4 mr-2" />
                  Add Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guest Portal Link Dialog */}
      <SendPortalLinkDialog
        open={portalLinkDialogOpen}
        onOpenChange={setPortalLinkDialogOpen}
        booking={portalLinkBooking}
        guest={portalLinkGuest}
        onSuccess={() => {}}
      />

      {/* Invoice Review Dialog */}
      <InvoiceReviewDialog
        open={invoiceReviewDialogOpen}
        onOpenChange={setInvoiceReviewDialogOpen}
        booking={invoiceReviewBooking}
        onInvoiceGenerated={() => loadData()}
      />

      {/* WhatsApp Send Dialog */}
      <Dialog open={whatsAppDialogOpen} onOpenChange={setWhatsAppDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <MessageCircle className="h-5 w-5 text-green-600" />
              Send Invoice via WhatsApp
            </DialogTitle>
            <DialogDescription>
              Enter mobile number(s) to send the invoice
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {whatsAppNumbers.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <Select
                  value={item.countryCode}
                  onValueChange={(value) => updateWhatsAppNumber(index, 'countryCode', value)}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countryCodes.map((cc) => (
                      <SelectItem key={cc.code} value={cc.code}>
                        {cc.code} {cc.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="tel"
                  placeholder="Mobile number"
                  value={item.number}
                  onChange={(e) => updateWhatsAppNumber(index, 'number', e.target.value)}
                  className="flex-1"
                  data-testid={`whatsapp-number-input-${index}`}
                />
                {whatsAppNumbers.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeWhatsAppNumber(index)}
                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addWhatsAppNumber}
              className="w-full border-dashed border-green-300 text-green-600 hover:bg-green-50"
              data-testid="add-whatsapp-number-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Another Number
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsAppDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmSendWhatsApp}
              disabled={sendingWhatsApp}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-whatsapp-send-btn"
            >
              {sendingWhatsApp ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Send via WhatsApp
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Send Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Mail className="h-5 w-5 text-blue-600" />
              Send Invoice via Email
            </DialogTitle>
            <DialogDescription>
              Enter email address(es) to send the invoice
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {emailAddresses.map((email, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => updateEmailAddress(index, e.target.value)}
                  className="flex-1"
                  data-testid={`email-address-input-${index}`}
                />
                {emailAddresses.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEmailAddress(index)}
                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addEmailAddress}
              className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50"
              data-testid="add-email-address-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Another Email
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmSendEmail}
              disabled={sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-email-send-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send via Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Cancel Booking
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {cancelBooking && (
            <div className="space-y-4">
              {/* Booking Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-900">
                  {getGuestName(cancelBooking.guest_id)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {getRoomInfo(cancelBooking.room_id, cancelBooking)}
                </p>
                <p className="text-sm text-gray-500">
                  {new Date(cancelBooking.check_in_date || cancelBooking.check_in).toLocaleDateString()} - {new Date(cancelBooking.check_out_date || cancelBooking.check_out).toLocaleDateString()}
                </p>
              </div>
              
              {/* Refund Amount */}
              <div className="space-y-2">
                <Label>Refund Amount</Label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">₹</span>
                  <Input
                    type="number"
                    value={cancelRefundAmount}
                    onChange={(e) => setCancelRefundAmount(e.target.value)}
                    placeholder="Enter refund amount"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Advance paid: ₹{cancelBooking.advance_paid?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              
              {/* Cancellation Reason */}
              <div className="space-y-2">
                <Label>Cancellation Reason</Label>
                <Input
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason for cancellation"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
            >
              Keep Booking
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelBooking}
              disabled={cancelling}
              data-testid="confirm-admin-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancellation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
